import { Category, CategoryService } from '@core/services/steelfab';
import { TranslateService } from '@core/translate/translate.service';
import { Component, DefineDependencies } from '@rhtml/component';
import { HostListener } from '@rhtml/decorators';
import { AngularLayout } from '@rhtml/modifiers';
import { Container, Inject } from '@rxdi/core';
import { deepCopy } from '@rxdi/graphql-client';
import { html, LitElement, property, state, unsafeCSS } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { ScrollToService } from '@shared/services';
import { languageFlagMap } from '@shared/styles/countries';
import { SteelFabTheme } from '@shared/styles/theme';
import * as NavbarComponentCSS from 'bundle-text:./navbar.component.css';
import { combineLatest, lastValueFrom } from 'rxjs';
import { delay, map, startWith, take, tap } from 'rxjs/operators';

import { SteelFabLogo } from './logo';

/**
 * @customElement navbar-component
 */
@Component({
  Settings: {
    selector: 'navbar-component',
    styles: [SteelFabTheme, unsafeCSS(NavbarComponentCSS)],
    modifiers: [...AngularLayout],
  },
  Providers: DefineDependencies(CategoryService, TranslateService)(Container),
  State(this: NavbarComponent, [categoryService, translateService]) {
    return combineLatest([categoryService.getCategoryTree(), translateService.currentLanguage.asObservable()]).pipe(
      map(([categories, currentLanguage]) => ({
        categories,
        currentLanguage,
        languages: translateService.config.languages,
      })),
      startWith({
        categories: [] as Category[],
        currentLanguage: localStorage.getItem('lang') ?? 'BG',
        languages: ['BG', 'EN'],
      })
    );
  },
  Render: ([, translateService]) =>
    function (this: NavbarComponent, state) {
      const { categories, currentLanguage, languages } = deepCopy(state);
      return html`
        <nav class="navbar" id="navbar">
          <div class="navbar-container">
            <div class="logo-section" @click=${() => this.goHome()}>${SteelFabLogo}</div>

            <div class="nav-links">
              <div class="nav-link" @click=${() => this.goto('/about')}>За нас</div>

              <div class="nav-item">
                <div class="nav-link" @click=${() => this.goto(`/products`)}>Продукти</div>
                <div class="dropdown">
                  ${categories.map(
                    (cat) => html`
                      <div class="dropdown-item" @click=${() => this.goto(`/products/${cat.slug}`)}>
                        <span ngIf=${cat.icon} class="dropdown-icon">${cat.icon}</span>
                        <img ngIf=${cat.logo?.url} class="dropdown-icon" src=${cat.logo?.url} />

                        ${cat.name}
                      </div>
                    `
                  )}
                </div>
              </div>

              <div class="nav-link" @click=${() => this.goto('/blog')}>Новини</div>
              <div class="nav-link" @click=${() => this.scrollToSection('partners-component')}>Партньори</div>
              <div class="nav-link" @click=${() => this.scrollToSection('contact-component')}>Контакти</div>
              <div class="language-switcher">
                <sl-dropdown placement="bottom-start" distance="8" hoist>
                  <button slot="trigger" class="language-trigger" type="button">
                    <img
                      class="language-flag"
                      .src=${languageFlagMap[currentLanguage]}
                      alt=${`${currentLanguage} flag`}
                    />
                    <span>${currentLanguage}</span>
                  </button>
                  <sl-menu>
                    ${languages.map(
                      (language) => html`
                        <sl-menu-item @click=${() => translateService.changeLanguage(language)}>
                          <img
                            slot="prefix"
                            class="language-menu-flag"
                            .src=${languageFlagMap[language]}
                            alt=${`${language} flag`}
                          />
                          ${language}
                        </sl-menu-item>
                      `
                    )}
                  </sl-menu>
                </sl-dropdown>
              </div>
              <button class="cta-button" @click=${() => this.scrollToSection('contact-component')}>Запитване</button>
            </div>

            <div class="hamburger" @click=${() => (this.mobileOpen = true)}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </nav>

        <!-- Mobile menu -->
        <div class="mobile-menu ${this.mobileOpen ? 'open' : ''}">
          <div class="mobile-close" @click=${() => (this.mobileOpen = false)}>✕</div>
          <div class="mobile-language-switcher">
            <sl-dropdown placement="bottom" distance="8" hoist>
              <button slot="trigger" class="language-trigger mobile-language-trigger" type="button">
                <img class="language-flag" .src=${languageFlagMap[currentLanguage]} alt=${`${currentLanguage} flag`} />
                <span>${currentLanguage}</span>
              </button>
              <sl-menu>
                ${languages.map(
                  (language) => html`
                    <sl-menu-item @click=${() => translateService.changeLanguage(language)}>
                      <img
                        slot="prefix"
                        class="language-menu-flag"
                        .src=${languageFlagMap[currentLanguage]}
                        alt=${`${language} flag`}
                      />
                      ${language}
                    </sl-menu-item>
                  `
                )}
              </sl-menu>
            </sl-dropdown>
          </div>
          <div class="mobile-link" @click=${() => this.mobileNav('/')}>Начало</div>
          <div class="mobile-link" @click=${() => this.mobileNav('/about')}>За нас</div>
          <div class="mobile-link" @click=${() => this.mobileNav('/products')}>Продукти</div>
          <div class="mobile-link" @click=${() => this.mobileNav('/blog')}>Новини</div>
          <div
            class="mobile-link"
            @click=${() => {
              this.mobileOpen = false;
              this.scrollToSection('contact-component');
            }}
          >
            Контакти
          </div>
        </div>
      `;
    },
})
export class NavbarComponent extends LitElement {
  @Router()
  private router: Router;

  @property({ type: Boolean })
  scrolled = false;

  @state()
  mobileOpen = false;

  @Inject(ScrollToService)
  private scrollToService: ScrollToService;

  @Inject(TranslateService)
  private translateService: TranslateService;

  private scrollThreshold = 80;

  @HostListener('window:scroll')
  onScroll() {
    const navbar = this.shadowRoot?.getElementById('navbar');
    if (!navbar) return;
    if (window.scrollY > this.scrollThreshold) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }
  }

  private goto(path: string) {
    this.router.go(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.mobileOpen = false;
  }

  private goHome() {
    this.router.go('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.mobileOpen = false;
  }

  private mobileNav(path: string) {
    this.mobileOpen = false;
    setTimeout(() => this.goto(path), 150);
  }

  private async scrollToSection(componentName: string) {
    if (this.router.activePath !== '/') {
      this.router.go('/');
      await lastValueFrom(
        this.router.onSnapshotChange().pipe(
          delay(300),
          take(1),
          tap(() => this.scrollToService.scrollToElement(componentName))
        )
      );
      return;
    }
    this.scrollToService.scrollToElement(componentName);
  }
}
